/**
 * Service Worker registration + Web Push subscription helpers.
 * Registered once on app boot; push subscription is opt-in (called from UI).
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  // Wait until window.load to avoid blocking first paint
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => console.warn("[SW] registration failed:", err));
  });
}

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const base64safe = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64safe);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getPushSubscriptionState() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { supported: false, subscribed: false };
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, subscribed: !!sub, subscription: sub };
}

export async function subscribeToPush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push not supported");
  }
  // Fetch VAPID public key
  const keyRes = await axios.get(`${API}/push/vapid-public-key`);
  const vapidKey = keyRes.data.public_key;
  if (!vapidKey) throw new Error("VAPID key not configured");

  const reg = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied");

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlB64ToUint8Array(vapidKey),
  });

  // Send to backend
  const token = localStorage.getItem("blumove_token");
  await axios.post(
    `${API}/push/subscribe`,
    sub.toJSON(),
    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
  );
  return sub;
}

export async function unsubscribeFromPush() {
  const { subscription } = await getPushSubscriptionState();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  try {
    await axios.post(`${API}/push/unsubscribe`, { endpoint });
  } catch (e) { /* noop */ }
}
